import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Calendar from '../components/Calendar';
import DailyFoodLog from '../components/DailyFoodLog';
import { useNutritionContext } from '../hooks/useNutritionContext';
import { getCentralDate } from '../utils/dateUtils';

const FoodTracker = () => {
  const { state, dispatch } = useNutritionContext();
  const [selectedDate, setSelectedDate] = useState(getCentralDate());
  const [selectedDateEntries, setSelectedDateEntries] = useState([]);

  // Force selectedDate to current date on mount
  useEffect(() => {
    const today = getCentralDate();
    setSelectedDate(today);
    dispatch({ type: 'SET_CURRENT_DATE', payload: today });
  }, []);

  // Update selected date entries when date or food entries change
  useEffect(() => {
    if (state.foodEntries.length > 0) {
      const dateEntries = state.foodEntries.filter(entry => entry.date === selectedDate);
      setSelectedDateEntries(dateEntries);
    } else {
      setSelectedDateEntries([]);
    }
  }, [selectedDate, state.foodEntries]);
  
  // Handle date selection from calendar
  const handleSelectDate = (date) => {
    setSelectedDate(date);
    dispatch({ type: 'SET_CURRENT_DATE', payload: date });
  };
  
  // Handle removing a food entry
  const handleRemoveEntry = (entryId) => {
    dispatch({ type: 'DELETE_FOOD_ENTRY', payload: entryId });
    dispatch({ type: 'SAVE_USER_DATA' });
  };

  const quickActions = [
    {
      title: 'Add Food Entry',
      description: 'Log a meal using USDA data or manual entry',
      link: '/add-food',
      color: 'bg-blue-50 text-blue-700'
    },
    {
      title: 'Macro Calculator',
      description: 'Recalculate personalized goals',
      link: '/macro-calculator',
      color: 'bg-purple-50 text-purple-700'
    },
    {
      title: 'Progress Reports',
      description: 'View daily, weekly, monthly trends',
      link: '/progress-reports',
      color: 'bg-emerald-50 text-emerald-700'
    }
  ];

  const userName = localStorage.getItem('userName') || '';

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {userName ? `Good day, ${userName.split(' ')[0]} 👋` : 'Dashboard'}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Track your nutrition and hit your goals.</p>
        </div>
        <Link
          to="/add-food"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-indigo-200 dark:shadow-indigo-900/30"
        >
          <span>+</span> Add Food Entry
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Main content */}
        <div className="lg:col-span-8 space-y-6">
          <DailyFoodLog
            entries={selectedDateEntries}
            date={selectedDate}
            onRemoveEntry={handleRemoveEntry}
            onDateChange={handleSelectDate}
          />
        </div>

        {/* Right column */}
        <div className="lg:col-span-4 space-y-5">
          <Calendar
            foodLog={state.foodEntries}
            onSelectDate={handleSelectDate}
            selectedDate={selectedDate}
          />

          {/* Quick Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">Quick Actions</h2>
            <div className="space-y-2">
              {quickActions.map((action, idx) => (
                <Link
                  key={idx}
                  to={action.link}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-indigo-200 dark:hover:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all group"
                >
                  <div>
                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-indigo-700 dark:group-hover:text-indigo-300">{action.title}</div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{action.description}</p>
                  </div>
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 flex-shrink-0 ml-2 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>
              ))}
            </div>
          </div>

          {/* USDA CTA */}
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-violet-600 text-white rounded-2xl p-5 shadow-lg">
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white/5 rounded-full" />
            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-200 mb-1">USDA FoodData Central</p>
              <h2 className="text-lg font-bold mb-2 leading-tight">Search Verified Nutrition Data</h2>
              <p className="text-sm text-indigo-100 mb-4 leading-relaxed">
                Thousands of foods with accurate macro and micronutrient data.
              </p>
              <Link
                to="/add-food"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-indigo-700 text-sm font-semibold rounded-lg hover:bg-indigo-50 transition-colors"
              >
                Search Database →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Medical Disclaimer */}
      <div className="mt-6 flex gap-2.5 p-3.5 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800/50">
        <span className="text-amber-500 flex-shrink-0 mt-0.5">⚠️</span>
        <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
          <strong>Medical Disclaimer:</strong> Nutrition targets shown are general estimates for informational purposes only and do not constitute medical advice, diagnosis, or treatment. Consult a licensed physician or registered dietitian before making significant dietary changes.
        </p>
      </div>
    </div>
  );
}

export default FoodTracker;
